/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Building2, Send, Shield, Users, CheckCircle, Briefcase } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CorporateProposalPage = () => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    employee_size: "",
    major_travel_services: "",
    monthly_travel_volume: "",
    gst_registered: "",
    requirements: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/corporate-enquiry`, formData);
      toast.success("Your corporate proposal request has been submitted! Our team will contact you within 24 hours.");
      setSubmitted(true);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const employeeSizes = [
    "1-10 Employees",
    "11-50 Employees",
    "51-200 Employees",
    "201-500 Employees",
    "501-1000 Employees",
    "1000+ Employees",
  ];

  const travelVolumes = [
    "1-5 trips/month",
    "6-15 trips/month",
    "16-30 trips/month",
    "31-50 trips/month",
    "50+ trips/month",
  ];

  const travelServices = [
    { value: "flights", label: "Flights Only" },
    { value: "hotels", label: "Hotels Only" },
    { value: "both", label: "Flights + Hotels" },
    { value: "full_service", label: "Full Service (Flights, Hotels, Transfers, MICE)" },
  ];

  if (submitted) {
    return (
      <div data-testid="corporate-proposal-success">
        <section className="relative pt-32 pb-20 bg-slate-900">
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/3678057/pexels-photo-3678057.png?w=1920"
              alt="Corporate Travel"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
          </div>
          <div className="container-custom relative z-10">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <h1 className="heading-2 text-white mb-4">Thank You!</h1>
              <p className="text-lg text-white/80 mb-8">
                Your corporate proposal request has been received. Our dedicated corporate travel team 
                will review your requirements and get in touch within 24 hours.
              </p>
              <Button
                onClick={() => window.location.href = "/"}
                className="btn-coral"
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="corporate-proposal-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900" data-testid="proposal-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3678057/pexels-photo-3678057.png?w=1920"
            alt="Corporate Travel"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="caption text-coral mb-4 block">Corporate & MICE</span>
            <h1 className="heading-1 text-white mb-6">Request Corporate Proposal</h1>
            <p className="text-lg text-white/80">
              Tell us about your organization's travel requirements and our corporate team 
              will prepare a customized proposal with competitive rates and tailored solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding bg-accent" data-testid="proposal-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <motion.div {...fadeInUp} className="lg:col-span-2">
              <Card className="shadow-xl" data-testid="proposal-form-card">
                <CardContent className="p-8">
                  <h2 className="heading-3 text-primary mb-2">Corporate Enquiry Form</h2>
                  <p className="text-slate-600 mb-8">
                    Please provide details about your company and travel requirements.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Company Info */}
                    <div>
                      <h3 className="font-heading font-semibold text-primary mb-4 flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-coral" />
                        Company Information
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="company_name">Company Name *</Label>
                          <Input
                            id="company_name"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleChange}
                            required
                            className="h-12"
                            placeholder="Your Company Name"
                            data-testid="input-company-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="employee_size">Employee Size</Label>
                          <Select
                            value={formData.employee_size}
                            onValueChange={(value) => handleSelectChange("employee_size", value)}
                          >
                            <SelectTrigger className="h-12" data-testid="select-employee-size">
                              <SelectValue placeholder="Select employee count" />
                            </SelectTrigger>
                            <SelectContent>
                              {employeeSizes.map((size) => (
                                <SelectItem key={size} value={size}>
                                  {size}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Contact Person */}
                    <div className="pt-6 border-t border-slate-200">
                      <h3 className="font-heading font-semibold text-primary mb-4 flex items-center gap-2">
                        <Users className="h-5 w-5 text-coral" />
                        Contact Details
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="contact_person">Contact Person *</Label>
                          <Input
                            id="contact_person"
                            name="contact_person"
                            value={formData.contact_person}
                            onChange={handleChange}
                            required
                            className="h-12"
                            placeholder="Full Name"
                            data-testid="input-contact-person"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Business Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="h-12"
                            placeholder="email@company.com"
                            data-testid="input-email"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="h-12 mt-2"
                          placeholder="+91 98765 43210"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    {/* Travel Requirements */}
                    <div className="pt-6 border-t border-slate-200">
                      <h3 className="font-heading font-semibold text-primary mb-4 flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-coral" />
                        Travel Requirements
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Major Travel Services Used</Label>
                          <RadioGroup
                            value={formData.major_travel_services}
                            onValueChange={(value) => handleSelectChange("major_travel_services", value)}
                            className="grid sm:grid-cols-2 gap-2"
                          >
                            {travelServices.map((service) => (
                              <div key={service.value} className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-slate-50 cursor-pointer">
                                <RadioGroupItem value={service.value} id={service.value} data-testid={`radio-${service.value}`} />
                                <Label htmlFor={service.value} className="cursor-pointer flex-1">
                                  {service.label}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="monthly_volume">Monthly Travel Volume</Label>
                          <Select
                            value={formData.monthly_travel_volume}
                            onValueChange={(value) => handleSelectChange("monthly_travel_volume", value)}
                          >
                            <SelectTrigger className="h-12" data-testid="select-monthly-volume">
                              <SelectValue placeholder="How many trips per month?" />
                            </SelectTrigger>
                            <SelectContent>
                              {travelVolumes.map((vol) => (
                                <SelectItem key={vol} value={vol}>
                                  {vol}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Is your company GST Registered?</Label>
                          <RadioGroup
                            value={formData.gst_registered}
                            onValueChange={(value) => handleSelectChange("gst_registered", value)}
                            className="flex gap-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="yes" id="gst-yes" data-testid="radio-gst-yes" />
                              <Label htmlFor="gst-yes" className="cursor-pointer">Yes</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="no" id="gst-no" data-testid="radio-gst-no" />
                              <Label htmlFor="gst-no" className="cursor-pointer">No</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="pt-6 border-t border-slate-200">
                      <div className="space-y-2">
                        <Label htmlFor="requirements">Additional Requirements</Label>
                        <Textarea
                          id="requirements"
                          name="requirements"
                          value={formData.requirements}
                          onChange={handleChange}
                          rows={4}
                          placeholder="Tell us more about your specific travel requirements, upcoming events, preferred destinations, or any special needs..."
                          data-testid="textarea-requirements"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="btn-coral w-full"
                      disabled={isSubmitting}
                      data-testid="submit-proposal-btn"
                    >
                      {isSubmitting ? "Submitting..." : "Request Corporate Proposal"}
                      <Send className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Why Us */}
              <Card className="shadow-lg" data-testid="proposal-benefits-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-primary mb-4">
                    Corporate Benefits
                  </h3>
                  <div className="space-y-4">
                    {[
                      { icon: Shield, text: "Dedicated Account Manager" },
                      { icon: CheckCircle, text: "Negotiated Corporate Rates" },
                      { icon: Briefcase, text: "Centralized Billing & GST Invoices" },
                      { icon: Users, text: "24/7 Travel Support" },
                    ].map((item) => (
                      <div key={item.text} className="flex items-center gap-3">
                        <item.icon className="h-5 w-5 text-coral flex-shrink-0" />
                        <span className="text-slate-600 text-sm">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Stats */}
              <Card className="bg-coral text-white" data-testid="proposal-stats-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold mb-4">Our Corporate Track Record</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-2xl font-bold">100+</div>
                      <div className="text-white/80 text-sm">Corporate Clients</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">₹50 Cr+</div>
                      <div className="text-white/80 text-sm">Annual Business Travel Managed</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">98%</div>
                      <div className="text-white/80 text-sm">Client Retention Rate</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact */}
              <Card className="shadow-lg" data-testid="proposal-contact-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-primary mb-4">
                    Need Immediate Assistance?
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Call our corporate sales team directly:
                  </p>
                  <a
                    href="tel:+918369033791"
                    className="flex items-center gap-2 text-coral hover:text-coral-dark font-semibold"
                  >
                    +91 8369033791
                  </a>
                  <p className="text-xs text-slate-500 mt-2">
                    Available Mon-Sat, 9 AM - 7 PM
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateProposalPage;
